import { Business, LocationResponse } from '@/features/business';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { axios } from '@/lib/axios';
import { ExtractFnReturnType, MutationConfig } from '@/lib/react-query';
import storage from '@/utils/storage';
import { showNotification } from '@/utils/notificationUtils';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';

type Options = {
  page?: number;
  perPage?: number;
};

type UseVersionOptions = {
  businessId?: number;
};
export type SaveLocationDTO = {
  data?: any;
};
export const getAllLocations = ({ page, perPage }: Options = {}): Promise<LocationResponse> => {
  return axios.get(`/business/locations/all`, {
    params: { page, perPage },
  });
};
export type DeletelocationDTO = {
  locationId: number;
};

export const deleteLocation = ({ locationId }: DeletelocationDTO) => {
  return axios.delete(`/business/locations/delete`, {
    headers: {
      'loc-id': locationId,
    },
  });
};

export type VersionResponse = {
  data: Version;
};

export type Version = {
  iosVersion?: string;
  androidVersion?: string;
  isForced?: boolean;
  title?: string;
  message?: string;
};

export type SaveVersionDTO = {
  data?: Version;
};

export type GetLocationDTO = {
  locationID?: number;
};
export const getLocation = ({ locationID }: GetLocationDTO = {}): Promise<Business> => {
  return axios.get(
    `/business/locations/${locationID ? locationID : storage.getKey('activeLocationId')}`,
    {
      params: {},
    }
  );
};
export const updateLocation = ({ data }: SaveLocationDTO): Promise<Business> => {
  return axios.post(`/business/locations/upsert`, data);
};
export const updatePrefillLocation = ({ data }: SaveLocationDTO): Promise<Business> => {
  return axios.post(`/business/locations/prefill`, data);
};
export const getDefaultLocation = (): string => {
  return storage.getKey('activeLocationId');
};
export const updateDefaultLocation = (locationId: string) => {
  return axios.get(`/business/location/users/active`, {
    params: {},
    headers: {
      'loc-id': locationId,
    },
  });
};

type QueryFnType = typeof getAllLocations;
type QueryFnType2 = typeof getLocation;

type UseUpdateLocationOptions = {
  config?: MutationConfig<typeof updateLocation>;
};
type UseGetLocationnOptions = {
  config?: MutationConfig<typeof getLocation>;
};
export const useUpdateLocation = ({ config }: UseUpdateLocationOptions = {}) => {
  const { refetchUser } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  return useMutation({
    onSuccess: async (data) => {
      await refetchUser();
      queryClient.invalidateQueries('Locations');
      queryClient.invalidateQueries('business');
      if (location && location?.pathname === '/businessinfo') {
        showNotification({
          type: 'success',
          title: 'Business info updated successfully',
        });
      }
    },
    onMutate: async ({ data }) => {
      await queryClient.cancelQueries('Locations');
      if (location && location?.pathname === '/new-location') {
        showNotification({
          type: 'success',
          title: 'New location added successfully',
        });
      }
    },
    ...config,
    mutationFn: updateLocation,
  });
};
export const useUpdatePrefillLocation = ({ config }: UseUpdateLocationOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: (data) => {
      queryClient.invalidateQueries('Locations');
      queryClient.invalidateQueries('business');
      showNotification({
        type: 'success',
        title: 'Business details updated',
      });
    },
    onMutate: async ({ data }) => {
      await queryClient.cancelQueries('Locations');
    },
    ...config,
    mutationFn: updatePrefillLocation,
  });
};
export const useSilentUpdateLocation = ({ config }: UseUpdateLocationOptions = {}) => {
  return useMutation({
    ...config,
    mutationFn: updateLocation,
  });
};
export const useSilentGetLocation = ({ config }: UseGetLocationnOptions = {}) => {
  return useMutation({
    mutationFn: getLocation,
  });
};
export const useLocations = ({ page, perPage }: Options = {}) => {
  return useQuery<ExtractFnReturnType<QueryFnType>>({
    queryKey: ['Locations'],
    queryFn: () => getAllLocations({ page, perPage }),
  });
};
export const useLocationById = ({ locationID }: GetLocationDTO = {}) => {
  return useQuery<ExtractFnReturnType<QueryFnType2>>({
    queryKey: ['Location'],
    queryFn: () => getLocation({ locationID }),
  });
};
export const useDefaultLocation = () => ({
  queryKey: ['selectedLocation'],
  queryFn: getDefaultLocation,
});

export const useChangeDefaultLocation = () => {
  const qc = useQueryClient();
  return useMutation<void, unknown, { locationId: string }, string>(
    async ({ locationId }) => {
      await updateDefaultLocation(locationId);
    },
    {
      onMutate: async ({ locationId }) => {
        await qc.cancelQueries('selectedLocation');

        const currentName: string | undefined = qc.getQueryData('selectedLocation');
        qc.setQueryData('selectedLocation', locationId);

        return currentName;
      },
      onError: (_error, _variables, context) => {
        if (context) qc.setQueryData('selectedLocation', context);
      },
      onSettled: async () => {
        qc.invalidateQueries('selectedLocation');
      },
    }
  );
};

type UseDeleteServiceOptions = {
  config?: MutationConfig<typeof deleteLocation>;
};

export const useDeleteLocation = ({ config }: UseDeleteServiceOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: (data) => {
      queryClient.invalidateQueries('Locations');
      showNotification({
        type: 'success',
        title: 'Location Deleted',
      });
    },
    onMutate: async ({ locationId }) => {
      await queryClient.cancelQueries('Locations');
    },
    ...config,
    mutationFn: deleteLocation,
  });
};

export type requestDTOUploadImage = {
  file: FormData;
};

export type UploadImageResponse = {
  imageUrl: string;
};

export const addBusinessPicture = ({
  file,
}: requestDTOUploadImage): Promise<UploadImageResponse> => {
  return axios.post('/business/upload-image', file);
};

type UseUpdateBusinessOptions1 = {
  config?: MutationConfig<typeof addBusinessPicture>;
};

export const useUploadPicture = ({ config }: UseUpdateBusinessOptions1 = {}) => {
  return useMutation({
    onSuccess: () => {},
    ...config,
    mutationFn: addBusinessPicture,
  });
};

export const getAppVersion = ({ businessId }: UseVersionOptions = {}): Promise<VersionResponse> => {
  return axios.get(`/mobile/appconfig`);
};

type UseUpdateBusinessOptionss = {
  config?: MutationConfig<typeof getAppVersion>;
};

export const useAppVersion = ({ config }: UseUpdateBusinessOptionss = {}) => {
  return useMutation({
    ...config,
    mutationFn: getAppVersion,
  });
};

export const updateAppVersion = ({ data }: SaveVersionDTO): Promise<Version> => {
  return axios.post(`/update/appconfig`, data);
};

type UseUpdateAppOptions = {
  config?: MutationConfig<typeof updateAppVersion>;
};

export const useEditAppConfig = ({ config }: UseUpdateAppOptions = {}) => {
  return useMutation({
    ...config,
    mutationFn: updateAppVersion,
  });
};
